/**
 * Bridge between the SentenceParserService output (tokens / phrases / clauses
 * / summary) and the rich annotation shape expected by SentenceVisualization
 * (lifted from the legacy prototype: tokens / collapsible / annotations).
 *
 * If parse_data already contains rich `annotations` and `collapsible`
 * (because the parser was upgraded to produce them directly), those win.
 * Otherwise this derives a reasonable rendering from phrases/clauses.
 */

const POS_LABEL = {
  NOUN: 'noun', PROPN: 'noun', PRON: 'pron.', VERB: 'verb', AUX: 'aux.',
  ADJ:  'adj.', ADV: 'adv.', ADP: 'prep.', DET: 'det.', CONJ: 'conj.',
  CCONJ: 'conj.', SCONJ: 'conj.', NUM: 'num.', PART: 'part.', INTJ: 'intj.',
  SYM: 'sym.', X: 'x', PUNCT: 'punct.',
};

const POS_COLOR = {
  NOUN: '#3b82f6', PROPN: '#3b82f6',
  PRON: '#a855f7',
  VERB: '#ef4444', AUX: '#ef4444',
  ADJ:  '#f97316', ADV: '#f97316',
  ADP:  '#06b6d4',
  DET:  '#6b7280',
  CONJ: '#eab308', CCONJ: '#eab308', SCONJ: '#eab308',
  NUM:  '#10b981',
  PART: '#9ca3af', INTJ: '#9ca3af', SYM: '#9ca3af', X: '#9ca3af',
};

// Phrase-type → underline color. Tuned to match the legacy palette.
const PHRASE_COLOR = {
  NP:   '#3b82f6', // noun phrases — blue
  VP:   '#ef4444', // verb phrases — red
  PP:   '#06b6d4', // prepositional phrases — cyan
  ADJP: '#f97316',
  ADVP: '#f97316',
  SBAR: '#a855f7', // subordinate-clause boundary — purple
};

// Clause-type → color + collapse style.
const CLAUSE_STYLE = {
  main:        { color: '#3b82f6', collapseAs: null },
  subordinate: { color: '#f59e0b', collapseAs: 'dependent' },
  relative:    { color: '#a855f7', collapseAs: 'relative' },
};

function tokenToLegacyPos(t) {
  if (!t) return 'word';
  if (t.pos === 'PUNCT') return 'punctuation';
  return (POS_LABEL[t.pos] || 'word').replace(/\.$/, ''); // strip trailing dot for the broad pos field
}

function phraseLabel(p) {
  if (!p) return '';
  return p.type;
}

export function adaptParseData(parseData, sentenceText) {
  const tokens = (parseData?.tokens || []).map((t) => ({
    id: t.i,
    text: t.text,
    pos: tokenToLegacyPos(t),
    posDetail: t.tag,
    lemma: t.lemma,
  }));

  // Pre-built annotations win when present (future: parser produces these).
  if (Array.isArray(parseData?.annotations) && parseData.annotations.length > 0) {
    return {
      sentence: sentenceText,
      tokens,
      collapsible: parseData.collapsible || [],
      annotations: parseData.annotations,
      summary: parseData.summary,
    };
  }

  const annotations = [];

  // ── POS highlights ─────────────────────────────────────────
  (parseData?.tokens || []).forEach((t) => {
    if (t.pos === 'PUNCT') return;
    annotations.push({
      id: `p-${t.i}`,
      layer: 'pos',
      type: 'highlight',
      label: POS_LABEL[t.pos] || t.pos?.toLowerCase() || '?',
      start: t.i,
      end: t.i,
      color: POS_COLOR[t.pos] || '#9ca3af',
    });
  });

  // ── Phrase underlines ──────────────────────────────────────
  (parseData?.phrases || []).forEach((p, i) => {
    annotations.push({
      id: `ph-${i}`,
      layer: 'phrases',
      type: 'underline',
      label: phraseLabel(p),
      start: p.start,
      end: p.end,
      color: PHRASE_COLOR[p.type] || '#6b7280',
      description: phraseDescription(p, tokens),
    });
  });

  // ── Clause underlines + subject→verb arrows ────────────────
  (parseData?.clauses || []).forEach((c, i) => {
    const style = CLAUSE_STYLE[c.type] || CLAUSE_STYLE.main;
    const start = Math.min(c.subject_start, c.verb_start);
    const end   = Math.max(c.subject_end, c.verb_end);

    annotations.push({
      id: `cl-${i}`,
      layer: 'clauses',
      type: 'underline',
      label: `${c.type} clause`,
      start,
      end,
      color: style.color,
      description: clauseDescription(c, tokens),
    });

    annotations.push({
      id: `cl-${i}-sv`,
      layer: 'clauses',
      type: 'arrow',
      label: 'subject → verb',
      start: c.subject_start,
      end: c.subject_end,
      target: c.verb_start,
      color: style.color,
    });
  });

  // ── Collapsibles: subordinate / relative clauses ───────────
  const collapsible = (parseData?.clauses || [])
    .map((c, i) => {
      const style = CLAUSE_STYLE[c.type];
      if (!style?.collapseAs) return null;
      const start = Math.min(c.subject_start, c.verb_start);
      const end   = Math.max(c.subject_end, c.verb_end);
      const summary = tokens.slice(start, end + 1).map((t) => t.text).join(' ');
      return {
        id: `col-${i}`,
        type: style.collapseAs,
        start,
        end,
        label: `${c.type} clause`,
        summary,
      };
    })
    .filter(Boolean);

  return {
    sentence: sentenceText,
    tokens,
    annotations,
    collapsible,
    summary: parseData?.summary,
  };
}

function phraseDescription(p, tokens) {
  const words = tokens.slice(p.start, p.end + 1).map((t) => t.text).join(' ');
  switch (p.type) {
    case 'NP':   return `Noun phrase: "${words}".`;
    case 'VP':   return `Verb phrase: "${words}".`;
    case 'PP':   return `Prepositional phrase: "${words}".`;
    case 'SBAR': return `Subordinate clause boundary: "${words}".`;
    case 'ADJP': return `Adjective phrase: "${words}".`;
    case 'ADVP': return `Adverb phrase: "${words}".`;
    default:     return `${p.type}: "${words}".`;
  }
}

function clauseDescription(c, tokens) {
  const subj = tokens.slice(c.subject_start, c.subject_end + 1).map((t) => t.text).join(' ');
  const verb = tokens.slice(c.verb_start, c.verb_end + 1).map((t) => t.text).join(' ');
  return `${capitalize(c.type)} clause — subject: "${subj}", verb: "${verb}".`;
}

function capitalize(s) {
  if (!s) return s;
  return s[0].toUpperCase() + s.slice(1);
}
